// React surface for the telemetry singleton.
//
// `<TelemetryProvider>` wires the auth attach/detach, subscribes the TanStack
// router for route-change events, installs window-level error +
// unhandledrejection listeners, and lazy-loads `web-vitals` on idle. All
// effects are no-ops until the user is authenticated — anonymous traffic
// produces zero telemetry rows.

import { useRouter } from '@tanstack/react-router';
import { type ReactNode,useEffect } from 'react';

import { useAuth } from './auth-context';
import { telemetry, track } from './telemetry';

type WebVitalsMetric = {
  value: number;
  id: string;
  rating?: 'good' | 'needs-improvement' | 'poor';
};

type WindowWithIdle = typeof window & {
  requestIdleCallback?: (cb: () => void) => number;
};

const ROUTE_CHANGE_DEBOUNCE_MS = 100;
const WEB_VITALS_FALLBACK_MS = 2000;

export function TelemetryProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const router = useRouter();
  const userId = user?.id ?? null;

  useEffect(() => {
    if (userId === null) {
      telemetry.detachAuth();
      return;
    }
    telemetry.attachAuth({ id: userId }, readCookie('wwaave_csrf'));
  }, [userId]);

  useEffect(() => {
    let timer: number | null = null;
    const unsub = router.subscribe('onResolved', () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        track('route.changed', { path: window.location.pathname });
      }, ROUTE_CHANGE_DEBOUNCE_MS);
    });
    return () => {
      unsub();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [router]);

  useEffect(() => {
    const onError = (ev: ErrorEvent) => {
      track('error.client', {
        message: String(ev.message ?? '').slice(0, 500),
        filename: ev.filename ?? null,
        lineno: ev.lineno ?? null,
        colno: ev.colno ?? null,
      });
    };
    const onRejection = (ev: PromiseRejectionEvent) => {
      const reason = ev.reason;
      const message = reason instanceof Error ? reason.message : String(reason);
      track('error.client', {
        kind: 'unhandledrejection',
        message: message.slice(0, 500),
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  useEffect(() => {
    if (userId === null) return;
    let cancelled = false;

    const load = async () => {
      try {
        const mod = await import('web-vitals');
        if (cancelled) return;
        const report =
          (name: string) =>
          (metric: WebVitalsMetric): void => {
            const props: Record<string, unknown> = {
              name,
              value: Math.round(metric.value * 100) / 100,
            };
            if (metric.rating !== undefined) props.rating = metric.rating;
            track('web_vitals.reported', props);
          };
        mod.onLCP(report('LCP'));
        mod.onCLS(report('CLS'));
        mod.onINP(report('INP'));
        mod.onTTFB(report('TTFB'));
        mod.onFCP(report('FCP'));
      } catch {
        // web-vitals failed to load — fail-open, rest of telemetry continues.
      }
    };

    const w = window as WindowWithIdle;
    let fallbackTimer: number | null = null;
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(() => {
        void load();
      });
    } else {
      fallbackTimer = window.setTimeout(() => {
        void load();
      }, WEB_VITALS_FALLBACK_MS);
    }

    return () => {
      cancelled = true;
      if (fallbackTimer !== null) window.clearTimeout(fallbackTimer);
    };
  }, [userId]);

  return <>{children}</>;
}

export function useTelemetry(): { track: typeof track } {
  return { track };
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  if (match === null || match[1] === undefined) return null;
  return decodeURIComponent(match[1]);
}
